import { formatChartMonthKey } from '@/components/Dashboard/operacaoChart.utils'
import type { DashboardData } from '@/hooks/dashboardDataTypes'

export type CaptacaoTabView = {
  totalLeads: number
  activeLeads: number
  qualifiedLeads: number
  convertedLeads: number
  nurturingLeads: number
  lostLeads: number
  conversionRate: number
  leadsByStatus: { status: string; count: number; label: string }[]
  leadsCreatedByMonth: { month: string; count: number }[]
}

const STATUS_LABELS: Record<string, string> = {
  NOVO: 'Novo',
  NEW: 'Novo',
  EM_CONTATO: 'Em contato',
  EM_PROSPECCAO: 'Em contato',
  TENTATIVA_1: 'Em contato',
  TENTATIVA_2: 'Em contato',
  EM_ANDAMENTO: 'Em contato',
  HANDOFF_PENDING: 'Em contato',
  QUALIFICADO: 'Qualificado',
  CONVERTIDO: 'Convertido',
  NUTRICAO: 'Nutrição',
  NURTURING: 'Nutrição',
  NUTRIÇÃO: 'Nutrição',
  PERDIDO: 'Perdido',
  DESCARTADO: 'Descartado',
  CANCELADO: 'Cancelado',
  ABANDONADO: 'Abandonado',
  AGUARDANDO: 'Aguardando',
}

function normalizeStatusKey(status: string): string {
  return status.trim().toUpperCase()
}

function statusLabel(status: string): string {
  const key = normalizeStatusKey(status)
  return STATUS_LABELS[key] ?? status
}

export function groupLeadsByStatusLabel(
  rows: { status: string; count: number }[],
): { status: string; count: number; label: string }[] {
  const buckets = new Map<string, { status: string; count: number; label: string }>()

  for (const row of rows) {
    const label = statusLabel(row.status)
    const existing = buckets.get(label)
    if (existing) {
      existing.count += row.count
    } else {
      buckets.set(label, { status: normalizeStatusKey(row.status), count: row.count, label })
    }
  }

  const order = [
    'Novo',
    'Em contato',
    'Qualificado',
    'Convertido',
    'Nutrição',
    'Aguardando',
    'Perdido',
    'Descartado',
    'Cancelado',
    'Abandonado',
  ]
  return [...buckets.values()].sort(
    (a, b) => order.indexOf(a.label) - order.indexOf(b.label) || a.label.localeCompare(b.label),
  )
}

function countStatuses(
  rows: { status: string; count: number }[],
  names: string[],
): number {
  const wanted = new Set(names)
  return rows.reduce(
    (sum, row) => (wanted.has(normalizeStatusKey(row.status)) ? sum + row.count : sum),
    0,
  )
}

export function mapCaptacaoTabView(data: DashboardData): CaptacaoTabView | null {
  const source = data.bdr ?? data.conversion
  if (!source) return null
  const byStatus = source.leadsByStatus ?? []
  const qualifiedLeads = countStatuses(byStatus, ['QUALIFICADO', 'SQL'])
  const nurturingLeads = countStatuses(byStatus, ['NUTRICAO', 'NURTURING', 'NUTRIÇÃO'])
  const lostLeads = countStatuses(byStatus, ['PERDIDO', 'DESCARTADO', 'CANCELADO', 'ABANDONADO'])
  const activeLeads = Math.max(0, source.totalLeads - source.convertedLeads - lostLeads)
  return {
    totalLeads: source.totalLeads,
    activeLeads,
    qualifiedLeads,
    convertedLeads: source.convertedLeads,
    nurturingLeads,
    lostLeads,
    conversionRate: source.conversionRate,
    leadsByStatus: groupLeadsByStatusLabel(byStatus),
    leadsCreatedByMonth: [],
  }
}

export function buildCaptacaoTrendData(monthly: CaptacaoTabView['leadsCreatedByMonth']) {
  return monthly.map((row) => ({
    month: formatChartMonthKey(row.month),
    leads: row.count,
  }))
}

export function hasCaptacaoTrendData(rows: { leads: number }[]): boolean {
  return rows.some((row) => row.leads > 0)
}

export function shouldShowCaptacaoContextBanner(view: CaptacaoTabView): boolean {
  return view.totalLeads === 0 && view.activeLeads > 0
}

export function formatCaptacaoConversionRate(rate: number, totalLeads: number, valuesVisible: boolean) {
  if (totalLeads === 0) {
    return { value: '—', hint: 'Sem leads novos no período' }
  }
  return {
    value: valuesVisible ? `${rate.toFixed(1)}%` : '•••',
    hint: 'Convertidos ÷ entradas na carteira closer',
  }
}
